import React, { ReactElement } from "react";
import { Route, Switch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AutoQuoteDetailsPage } from "pages/autoQuote/AutoQuoteDetailsPage/AutoQuoteDetailsPage";
import HomePage from "../pages/HomePage/HomePage";
import ServiceInfoPage from "../pages/HomePage/SubPages/ServiceInfoPage";
import { SignUpPage } from "../pages/SignUpPage/SignUpPage";
import { CreateProjectFromExternalLinkPage } from "../pages/CreateProjectPage/CreateProjectFromExternalLinkPage/CreateProjectFromExternalLinkPage";
import { CreateProjectPage } from "../pages/CreateProjectPage/CreateProjectPage";
import AppPlaceholder from "../pages/HomePage/SubPages/AppPlaceholder";
import { EmailConfirmationPage } from "../pages/EmailConfirmationPage/EmailConfirmationPage";
import IFrameViewer from "../pages/HomePage/SubPages/IFrameViewer";
import { ChangePasswordPage } from "../pages/ChangePasswordPage/ChangePasswordPage";
import { ReportsPage } from "../pages/ReportsPage/ReportsPage";
import { ReportDetailsPage } from "../pages/ReportsPage/ReportDetailsPage/ReportDetailsPage";
import { PowerBiReportsPage } from "../pages/PowerBiReportsPage/PowerBiReportsPage";
import { PowerBiReportEmbedPage } from "../pages/PowerBiReportsPage/PowerBiReportEmbedPage/PowerBiReportEmbedPage";
import {
  MachineTranslationPage,
  MachineTranslationTabId
} from "../pages/MachineTranslationPage/MachineTranslationPage";
import { RestrictedPage } from "./RestrictedPage/RestrictedPage";
import { UserGroup } from "../gen/clients/llts";
import { CoupaStarterPage } from "../pages/coupa/CoupaStarterPage";
import { CreateCoupaQuotePage } from "../pages/coupa/CreateCoupaQuotePage/CreateCoupaQuotePage";
import { ErrorPage } from "./ErrorPage/ErrorPage";
import { CoupaErrorPage } from "../pages/coupa/CoupaErrorPage/CoupaErrorPage";
import { CoupaRequestsPage } from "../pages/coupa/CoupaRequestsPage/CoupaRequestsPage";
import { JobRequestDetailsPage } from "../pages/coupa/CoupaRequestsPage/JobRequestDetailsPage/JobRequestDetailsPage";
import { InternalLoginPage } from "../pages/InternalLoginPage/InternalLoginPage";
import { PageLayout } from "./PageLayout/PageLayout";
import LocaleManager from "./LocaleManager/LocaleManager";
import ApiBaseUrlManager from "./ApiBaseUrlManager/ApiBaseUrlManager";

const Routes = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <ApiBaseUrlManager />
      <LocaleManager />
      <Switch>
        <Route path="/:lltsBranchId?/languageLineConnect">
          <PageLayout title={t("pageTitles.languageLineConnect")}>
            <AppPlaceholder id="languageLineConnect" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/quickTranslation">
          <PageLayout title={t("pageTitles.quickTranslation")}>
            <AppPlaceholder id="quickTranslation" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/connector">
          <PageLayout title={t("pageTitles.connector")}>
            <AppPlaceholder id="connector" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/websiteProxy">
          <PageLayout title={t("pageTitles.websiteProxy")}>
            <AppPlaceholder id="websiteProxy" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/consoleHelp">
          <PageLayout title={t("pageTitles.consoleHelp")}>
            <IFrameViewer id="consoleHelp" fileName="Frequently-Asked-Questions.html" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/languageConnect">
          <PageLayout title={t("pageTitles.languageConnect")}>
            <ServiceInfoPage id="languageConnect" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/webSitesEasyWay">
          <PageLayout title={t("pageTitles.webSitesEasyWay")}>
            <ServiceInfoPage id="webSitesEasyWay" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/anythingInBetween">
          <PageLayout title={t("pageTitles.anythingInBetween")}>
            <ServiceInfoPage id="anythingInBetween" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/solutionWizard">
          <PageLayout title={t("pageTitles.solutionWizard")}>
            <ServiceInfoPage id="solutionWizard" />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/signUp" exact={true}>
          <PageLayout title={t("pageTitles.signUp")}>
            <SignUpPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/signUp/:clientId/:token" exact={true}>
          <PageLayout title={t("pageTitles.signUp")}>
            <SignUpPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/confirmEmail" exact={false}>
          <PageLayout title={t("pageTitles.confirmEmail")}>
            <EmailConfirmationPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/createProject/:clientId/:token">
          <PageLayout title={t("pageTitles.createProject")}>
            <CreateProjectFromExternalLinkPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/createProject" exact>
          <PageLayout title={t("pageTitles.createProject")}>
            <CreateProjectPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/changePassword" exact={true}>
          <PageLayout title={t("pageTitles.changePassword")}>
            <ChangePasswordPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/reports" exact={true}>
          <PageLayout title={t("pageTitles.reports")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["reports"]}
            >
              <ReportsPage />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/reports/:reportId">
          <PageLayout title={t("pageTitles.reportDetails")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["reports"]}
            >
              <ReportDetailsPage />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/pbiReports" exact={true}>
          <PageLayout title={t("pageTitles.pbiReports")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["pbiReports"]}
            >
              <PowerBiReportsPage />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/pbiReports/:reportId">
          <PageLayout title={t("pageTitles.pbiReportDetails")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["pbiReports"]}
            >
              <PowerBiReportEmbedPage />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/machineTranslation" exact={true}>
          <PageLayout title={t("pageTitles.machineTranslation")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["machineTranslation"]}
            >
              <MachineTranslationPage />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path={`/:lltsBranchId?/machineTranslation/${MachineTranslationTabId.textTranslation}`}>
          <PageLayout title={t("pageTitles.machineTranslationTextTranslation")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["machineTranslation"]}
            >
              <MachineTranslationPage selectedTabId={MachineTranslationTabId.textTranslation} />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path={`/:lltsBranchId?/machineTranslation/${MachineTranslationTabId.fileTranslation}/:jobRequestId?`}>
          <PageLayout title={t("pageTitles.machineTranslationFileTranslation")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["machineTranslation"]}
            >
              <MachineTranslationPage selectedTabId={MachineTranslationTabId.fileTranslation} />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path={`/:lltsBranchId?/machineTranslation/${MachineTranslationTabId.mediaTranslation}/:jobRequestId?`}>
          <PageLayout title={t("pageTitles.machineTranslationMediaTranslation")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["machineTranslation"]}
            >
              <MachineTranslationPage selectedTabId={MachineTranslationTabId.mediaTranslation} />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path={`/:lltsBranchId?/machineTranslation/${MachineTranslationTabId.aiToolbox}/:jobRequestId?`}>
          <PageLayout title={t("pageTitles.machineTranslationAiToolbox")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["machineTranslation"]}
            >
              <MachineTranslationPage selectedTabId={MachineTranslationTabId.aiToolbox} />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/coupa/starter/:requestId" exact={true}>
          <PageLayout title={t("pageTitles.coupaStarter")}>
            <CoupaStarterPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/coupa/createQuote" exact={true}>
          <PageLayout title={t("pageTitles.coupaCreateQuote")}>
            <RestrictedPage
              allowedRoles={[UserGroup.CLIENT, UserGroup.CLIENT_EMPLOYEE]}
              requiredApplications={["coupa"]}
            >
              <CreateCoupaQuotePage />
            </RestrictedPage>
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/coupa/error" exact={true}>
          <PageLayout title={t("pageTitles.coupaError")}>
            <CoupaErrorPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/coupa/:jobRequestId" exact={true}>
          <PageLayout title={t("pageTitles.coupaJobRequest")}>
            <JobRequestDetailsPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/coupa/createQuote" exact={true}>
          <PageLayout title={t("pageTitles.coupaCreateQuote")}>
            <ErrorPage apiError={{ message: "Invalid page URL" }} />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/coupa" exact={false}>
          <PageLayout title={t("pageTitles.coupaRequests")}>
            <CoupaRequestsPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/quotes/:jobRequestId" exact={true}>
          <PageLayout title={t("pageTitles.quoteDetails")}>
            <AutoQuoteDetailsPage />
          </PageLayout>
        </Route>
        <Route path="/:lltsBranchId?/internalLogin/:username?" exact={true}>
          <InternalLoginPage />
        </Route>
        <Route path="/:lltsBranchId?">
          <PageLayout hideSignInButtonInHeader={true} title={t("pageTitles.home")}>
            <HomePage id="homePage" />
          </PageLayout>
        </Route>
      </Switch>
    </>
  );
};

export default Routes;
